import { createAsyncThunk } from "@reduxjs/toolkit";
import { msgid, ngettext, t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { submitReview as submitReviewAPI } from "../../../api/reviews";
import { runValidator } from "../../../forms/validation";
import { IFormUUID } from "../../../models/nominals";
import { IWriteReviewTemplate } from "../../../models/reviews";
import { updateGTMUserHash } from "../../../utils/analytics";
import { objectKeys } from "../../../utils/functional";
import { getPageSetting } from "../../../utils/settings";
import { REVIEW_VARIANT_NOT_SELECTED, SubmitReviewStatus } from "../constants";
import { IWriteReviewFormState } from "../models";

const getCustomRequiredMessage = (
    fieldName: string,
    isMultiReview?: boolean,
) => {
    const reviewCount = isMultiReview ? 2 : 1;
    switch (fieldName) {
        case "rating":
            return t`Please select a star rating`;
        case "name":
            return t`Please enter a nickname`;
        case "headline":
            return ngettext(
                msgid`Please enter a headline`,
                `Please enter a headline. If you do not wish to review this product, please uncheck the box above.`,
                reviewCount,
            );
        case "comments":
            return ngettext(
                msgid`Please describe your experience`,
                `Please describe your experience. If you do not wish to review this product, please uncheck the box above.`,
                reviewCount,
            );
        case "location":
            return t`Please enter your location`;
        case "bottomline":
            return ngettext(
                msgid`Please choose yes or no`,
                `Please choose yes or no. If you do not wish to review this product, please uncheck the box above.`,
                reviewCount,
            );
        case "email_collection":
            return t`Please enter your email address`;
        default:
            return t`This field is required`;
    }
};

const getReviewDisclosureParams = () => {
    const window = getWindow();
    if (!window) {
        return { source: "web", disclosure_code: "" };
    }
    const url = new URL(window.location.href);
    return {
        source: url.searchParams.get("source") || "web",
        disclosure_code: url.searchParams.get("disclosure_code") || "",
    };
};

const getReviewSiteSource = () => {
    const app_name = getPageSetting("app_display_name");
    return {
        review_source: app_name || "",
    };
};

const clearProductErrors = (errors: { [key: string]: string[] }) => {
    const multiReviewRequiredFields = [
        "headline",
        "rating",
        "comments",
        "bottomline",
    ];

    const hasErrorMsg = (key: string) => {
        return errors[key].length > 0;
    };

    const incompleteForm = multiReviewRequiredFields.every(hasErrorMsg);

    if (incompleteForm) {
        multiReviewRequiredFields.forEach((field) => {
            errors[field] = [];
        });
    }
};

/**
 * Validate and submit a review to the API.
 *
 * This thunk handles the complete review submission workflow:
 * 1. Validates that a product and variant are selected
 * 2. Validates all required template fields (rating, headline, comments, etc.)
 * 3. Validates email format if provided
 * 4. Handles multi-review forms (allows skipping products with all fields blank)
 * 5. Adds disclosure parameters and site source to submission data
 * 6. Updates GTM user hash if email provided
 * 7. Submits the review to PowerReviews API
 * 8. Returns success/failure status with any validation errors
 *
 * @param {Object} params - The parameters object
 * @param {IFormUUID} params.formUUID - The unique identifier for the form instance
 * @param {IWriteReviewTemplate} params.template - The review template defining required fields
 * @param {IWriteReviewFormState} params.form - The current form state with user input
 * @param {boolean} [params.isMultiReview] - True if this is a multi-product review form
 *
 * @returns {Promise} Promise resolving to an object containing:
 *   - formUUID: The form identifier (passed through for reducer)
 *   - status: SubmitReviewStatus enum (SUCCESS, FAIL_INVALID_FIELDS, etc.)
 *   - errors: Validation errors object (field name -> error messages)
 *   - showErrors: Whether to display validation errors to user
 *   - showProductSelectError: Whether to show product selection error
 *   - showVariantSelectError: Whether to show variant selection error
 *
 * @example
 * // Submit review from form component
 * const result = await dispatch(submitReview({
 *   formUUID: 'form-123',
 *   template: reviewTemplate,
 *   form: formState,
 *   isMultiReview: false
 * }));
 *
 * @sideEffects
 * - Updates `ui.writeReviewForms[formUUID].mode` to THANK_YOU_OPEN on success via extraReducers
 * - Updates `ui.writeReviewForms[formUUID].errors` with validation errors on failure via extraReducers
 * - Updates GTM dataLayer with user hash if email provided
 * - Logs errors to console on API failure
 */
export const submitReview = createAsyncThunk<
    {
        formUUID: IFormUUID;
        status: SubmitReviewStatus;
        errors?: IWriteReviewFormState["errors"];
        showErrors?: boolean;
        showProductSelectError?: boolean;
        showVariantSelectError?: boolean;
    },
    {
        formUUID: IFormUUID;
        template: IWriteReviewTemplate;
        form: IWriteReviewFormState;
        isMultiReview?: boolean;
    }
>(
    "reviews/submitReview",
    async ({ formUUID, template, form, isMultiReview }) => {
        // Check if a product is selected
        if (!form.selectedProductID) {
            console.error(
                "Can not submit review without first selecting a product",
            );
            return {
                formUUID,
                status: SubmitReviewStatus.FAIL_MISSING_PRODUCT_SELECTION,
                errors: {},
                showErrors: false,
                showProductSelectError: true,
                showVariantSelectError: false,
            };
        }

        // Check if a variant is selected
        if (form.selectedVariant === REVIEW_VARIANT_NOT_SELECTED) {
            console.error(
                "Can not submit review without first selecting a variant or 'not-sure'",
            );
            return {
                formUUID,
                status: SubmitReviewStatus.FAIL_MISSING_VARIANT_SELECTION,
                errors: {},
                showErrors: false,
                showProductSelectError: false,
                showVariantSelectError: true,
            };
        }

        // Validate the form data
        // Create a mutable copy of the form data by converting readonly arrays to mutable arrays
        const formData: { [key: string]: string | string[] } = {
            ...form.valuesText,
        };
        // Copy valuesMultiSelect, converting readonly arrays to mutable arrays
        for (const key in form.valuesMultiSelect) {
            formData[key] = [...form.valuesMultiSelect[key]];
        }
        const errors: { [key: string]: string[] } = {};
        let errorCount = 0;
        objectKeys(template).forEach((tmplKey) => {
            const tmplField = template[tmplKey];
            errors[tmplKey] = [];

            const textValue: string | string[] = formData[tmplKey];

            // Check if the text is empty of is made of whitespace characters only
            // PowerReviews API expects RequiredField to have at least one non-whitespace character
            const isBlank = !textValue || !textValue.toString().trim();

            if (tmplField.is_required && isBlank) {
                errors[tmplKey].push(
                    getCustomRequiredMessage(tmplKey, isMultiReview),
                );
                errorCount++;
            }

            // Validate the email address
            if (tmplField.type === "email" && formData[tmplKey]) {
                const email = formData[tmplKey] as string;
                if (runValidator("email", email)) {
                    errors[tmplKey].push(t`Please enter a valid email address`);
                    errorCount++;
                }
            }
        });

        // clear errors for product if all fields left blank
        if (isMultiReview) {
            clearProductErrors(errors);
        }

        // If validation failed, return errors
        if (errorCount > 0) {
            return {
                formUUID,
                status: SubmitReviewStatus.FAIL_INVALID_FIELDS,
                errors: errors,
                showErrors: true,
                showProductSelectError: false,
                showVariantSelectError: false,
            };
        }

        const reviewData: typeof formData = {
            ...formData,
            variant: form.selectedVariant,
            ...getReviewDisclosureParams(),
            ...getReviewSiteSource(),
        };

        // Update USER_HASH in the datalayer
        if ("email_collection" in reviewData) {
            updateGTMUserHash(reviewData.email_collection.toString());
        }

        // Submit Review
        try {
            await submitReviewAPI(
                form.selectedProductID,
                form.started,
                reviewData,
            );
        } catch (e2) {
            // Log errors to console
            console.log(e2);
            return {
                formUUID,
                status: SubmitReviewStatus.FAIL_API,
            };
        }

        // Success
        return {
            formUUID,
            status: SubmitReviewStatus.SUCCESS,
        };
    },
);
